import { useCallback, useEffect, useState } from 'react';
import { SECTION_PATHS, sectionForPath } from './routes';
import './App.css';
import { PALETTES, MOBILE_EDGE, MOBILE_PANEL_LEFT, MOBILE_PANEL_TOP, MOBILE_PANEL_BOTTOM } from './theme';
import { PROJECTS, CATEGORIES, SKILLS } from './data/portfolio';
import useIsMobile from './hooks/useIsMobile';
import ParticleField from './components/ParticleField';
import Grain from './components/Grain';
import NavRail from './components/NavRail';
import CategoryNav from './components/CategoryNav';
import HomeView from './components/HomeView';
import ContactView from './components/ContactView';
import ProjectsPanel from './components/ProjectsPanel';
import SkillsPanel from './components/SkillsPanel';
import Intro from './components/Intro';
import Blog from './components/Blog';
import PageTransition from './components/PageTransition';

const VIEW_BASE = {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-end',
    textAlign: 'right',
};

/** Right-column views cross-fade; the outgoing one drops out of the hit area. */
const viewStyle = (active) =>
    active
        ? {
                ...VIEW_BASE,
                opacity: 1,
                transform: 'translateY(0)',
                pointerEvents: 'auto',
                transition: 'opacity .55s ease, transform .55s ease',
            }
        : {
                ...VIEW_BASE,
                opacity: 0,
                transform: 'translateY(14px)',
                pointerEvents: 'none',
                transition: 'opacity .3s ease, transform .3s ease',
            };

export default function App({ theme = 'dark' }) {
    return <PageTransition>{(path, navigate) =>
        path === '/blog' || path.startsWith('/blog/')
            ? <Blog path={path} theme={theme} />
            : <Portfolio theme={theme} view={sectionForPath(path) || 'home'} navigate={navigate} />
    }</PageTransition>;
}

function Portfolio({ theme, view, navigate }) {
    const [introVisible, setIntroVisible] = useState(true);
    const finishIntro = useCallback(() => setIntroVisible(false), []);
    const [category, setCategory] = useState(null);
    const [open, setOpen] = useState(null);
    const [hover, setHover] = useState(null);

    const mobile = useIsMobile();
    const edge = mobile ? MOBILE_EDGE : 68;
    const palette = PALETTES[theme] || PALETTES.dark;
    const projectsShown = view === 'projects';
    // Both list views fill the right column, so the chip would sit on top of them.
    const chipHidden = projectsShown || view === 'skills';

    // Stable identity: the panel subscribes a document listener against it.
    const closeProject = useCallback(() => setOpen(null), []);

    useEffect(() => {
        if (view !== 'projects') setOpen(null);
        setHover(null);
    }, [view]);

    // A project badge on the Skills page jumps to that project, expanded. The
    // category filter is cleared first, or a filter left on an unrelated category
    // would hide the very project being opened.
    const openProjectFromSkill = (id) => {
        setCategory(null);
        setHover(null);
        setOpen(id);
        navigate(SECTION_PATHS.projects);
    };

    // Mobile puts availability next to the contact details.
    const chip = (
        <div
            style={{
                display: 'flex',
                alignItems: 'center',
                gap: '9px',
                fontSize: mobile ? '10px' : '11px',
                letterSpacing: '.16em',
                textTransform: 'uppercase',
                opacity: chipHidden ? 0 : 0.75,
                fontWeight: 500,
                textShadow: palette.microShadow,
                pointerEvents: 'none',
                transition: 'opacity .4s ease',
                ...(mobile
                    ? { justifyContent: 'center', marginBottom: '4px', letterSpacing: '.1em' }
                    : { position: 'absolute', right: '68px', top: '64px' }),
            }}
        >
            <span
                style={{
                    width: '7px',
                    height: '7px',
                    borderRadius: '50%',
                    background: 'var(--accent)',
                    boxShadow: '0 0 9px var(--accent)',
                    flex: '0 0 auto',
                }}
            />
            Open to full-time roles
        </div>
    );

    return (
        <>
        {introVisible && <Intro onComplete={finishIntro} />}
        <div
            className="sig-app"
            inert={introVisible}
            style={{
                position: 'fixed',
                inset: 0,
                height: mobile ? '100dvh' : undefined,
                background: theme === 'dark' ? '#060708' : '#e8e5df',
                fontFamily: "'Hanken Grotesk',sans-serif",
                overflow: 'hidden',
            }}
        >
            <ParticleField theme={theme} dimmed={view !== 'home'} mobile={mobile} />
            <Grain />

            <div
                style={{
                    position: 'absolute',
                    inset: 0,
                    zIndex: 3,
                    pointerEvents: 'none',
                    background: palette.scrim,
                }}
            />

            <div
                style={{
                    position: 'absolute',
                    inset: 0,
                    zIndex: 5,
                    pointerEvents: 'none',
                    color: palette.color,
                    fontFamily: "'Hanken Grotesk',sans-serif",
                    '--accent': palette.accent,
                    textShadow: palette.textShadow,
                }}
            >
                <div
                    style={{
                        position: 'absolute',
                        inset: mobile ? '12px' : '18px',
                        border: '1px solid',
                        borderColor: 'color-mix(in oklab, currentColor 14%, transparent)',
                        pointerEvents: 'none',
                    }}
                />

                <div
                    style={{
                        position: 'absolute',
                        top: mobile ? 'calc(36px + env(safe-area-inset-top, 0px))' : '60px',
                        left: `${edge}px`,
                        right: mobile ? `${edge}px` : undefined,
                    }}
                >
                    <h1
                        style={{
                            margin: 0,
                            // The wordmark and its label are the only Neue Montreal on
                            // the page; the rest of the interface keeps its own pair.
                            fontFamily: "'Neue Montreal',sans-serif",
                            fontWeight: 300,
                            fontSize: mobile
                                ? 'clamp(30px,9.5vw,46px)'
                                : 'clamp(42px,5.6vw,74px)',
                            lineHeight: 0.98,
                            letterSpacing: '.01em',
                            color: 'inherit',
                        }}
                    >
                        Jonathan Edward
                    </h1>
                    <div
                        style={{
                            fontFamily: "'Neue Montreal',sans-serif",
                            // The wordmark's line box already carries descender space
                            // above this, so the visible gap runs wider than the value.
                            marginTop: '2px',
                            fontSize: mobile ? '10px' : '12px',
                            letterSpacing: '.24em',
                            textTransform: 'uppercase',
                            opacity: 0.72,
                            fontWeight: 600,
                            textShadow: palette.microShadow,
                        }}
                    >
                        Electrical Engineering / Software / Systems
                    </div>
                </div>

                {/* status chip, hidden on Projects so it doesn't collide with the list */}
                {!mobile && chip}

                <NavRail view={view} />

                {/* Portrait keeps the list whole: a filter row there costs more
                    height than it saves scrolling. */}
                {!mobile && (
                    <CategoryNav
                        categories={CATEGORIES}
                        active={category}
                        shown={projectsShown}
                        onSelect={(c) => {
                            setCategory((cur) => (cur === c ? null : c));
                            setOpen(null);
                        }}
                    />
                )}

                {/* Keep each view's geometry stable throughout its cross-fade. */}
                <div
                    className="sig-view-column sig-view-column-home"
                    style={{ position: 'absolute', pointerEvents: view === 'home' ? 'auto' : 'none' }}
                >
                    <HomeView style={viewStyle(view === 'home')} active={view === 'home'} mobile={mobile} />
                </div>

                <div
                    className="sig-view-column"
                    style={{
                        position: 'absolute',
                        right: `${edge}px`,
                        left: mobile ? `${MOBILE_PANEL_LEFT}px` : undefined,
                        top: mobile ? `calc(${MOBILE_PANEL_TOP}px + env(safe-area-inset-top, 0px))` : undefined,
                        bottom: mobile ? `calc(${MOBILE_PANEL_BOTTOM}px + env(safe-area-inset-bottom, 0px))` : '60px',
                        width: mobile ? 'auto' : 'min(400px,40vw)',
                        height: mobile ? 'auto' : '58vh',
                        pointerEvents: view === 'contact' ? 'auto' : 'none',
                    }}
                >
                    <ContactView style={viewStyle(view === 'contact')} active={view === 'contact'} mobile={mobile}>
                        {mobile && chip}
                    </ContactView>
                </div>

                <ProjectsPanel
                    projects={PROJECTS}
                    category={category}
                    open={open}
                    shown={projectsShown}
                    onToggle={(short) => setOpen((cur) => (cur === short ? null : short))}
                    onClose={closeProject}
                    palette={palette}
                />

                <SkillsPanel
                    skills={SKILLS}
                    hover={hover}
                    onHover={setHover}
                    shown={view === 'skills'}
                    palette={palette}
                    onProjectClick={openProjectFromSkill}
                />
            </div>
        </div>
        </>
    );
}
